import type { SubsSection, SubRowVariant, SubVisualVariant } from '@/msgdeskRuntime/state'
import type {
  LegacyRowViewType,
  LegacyRowViewValue,
  RowVariantDefinition,
  RowVariantMetadata,
  RowVariantRegistry,
} from './RowTemplateTypes'

export const legacyRowViewTypes: Readonly<Record<LegacyRowViewValue, LegacyRowViewType>> = {
  1: 'compact',
  2: 'comments',
  3: 'gallery',
  4: 'gallery_uno',
  5: 'chat',
  6: 'gallery_width',
  7: 'gallery_height',
  8: 'deck',
  9: 'tile',
}

export const sharedChatAnswerVariantMetadata = {
  value: 5,
  viewType: 'chat',
  label: 'Chat',
  variant: 'chat',
} as const satisfies RowVariantMetadata

export function defineRowVariantRegistry(registry: RowVariantRegistry): RowVariantRegistry {
  for (const section of ['answers', 'submsges'] as const) {
    const values = new Set<number>()
    const viewTypes = new Set<string>()
    const variants = new Set<SubVisualVariant>()

    for (const choice of registry[section].variants) {
      if (legacyRowViewTypes[choice.value] !== choice.viewType) {
        throw new Error(`Row variant ${section}:${choice.label} mismatches legacy value ${choice.value}`)
      }
      if (values.has(choice.value) || viewTypes.has(choice.viewType) || variants.has(choice.variant)) {
        throw new Error(`Duplicate row variant mapping in ${section}: ${choice.label}`)
      }
      if (typeof choice.Template !== 'function') {
        throw new Error(`Row variant ${section}:${choice.label} has no renderer component`)
      }
      if (section === 'submsges' && (choice.value === 5 || choice.viewType === 'chat' || choice.variant === 'chat')) {
        throw new Error('Chat is an answers-only row variant')
      }
      values.add(choice.value)
      viewTypes.add(choice.viewType)
      variants.add(choice.variant)
    }

    if (!variants.has(registry[section].fallback)) {
      throw new Error(`Row variant fallback is not registered in ${section}: ${registry[section].fallback}`)
    }
  }

  const chat = registry.answers.variants.find(choice => choice.variant === 'chat')
  if (!chat || chat.value !== 5 || chat.viewType !== 'chat') {
    throw new Error('Answers registry must include shared Chat mapped to legacy value 5 / chat')
  }

  return registry
}

export function rowVariantOptions(
  registry: RowVariantRegistry,
  section: SubsSection,
): Array<[LegacyRowViewValue, string]> {
  return registry[section].variants.map(({ value, label }) => [value, label])
}

export function rowVariantFallbackLabel(registry: RowVariantRegistry, section: SubsSection): string {
  return registry[section].variants.find(choice => choice.variant === registry[section].fallback)?.label ?? 'local default'
}

export function resolveRegisteredRowVariant(
  registry: RowVariantRegistry,
  section: SubsSection,
  requested: SubRowVariant,
  viewType: unknown,
  projectedVariant?: SubVisualVariant,
): RowVariantDefinition {
  const sectionRegistry = registry[section]
  const fallback = sectionRegistry.variants.find(choice => choice.variant === sectionRegistry.fallback)
  if (!fallback) throw new Error(`Row variant fallback is missing in ${section}`)

  if (requested !== 'auto' && requested !== 'legacy') {
    return sectionRegistry.variants.find(choice => choice.variant === requested) ?? fallback
  }

  const normalized = String(viewType ?? '').trim().toLowerCase()
  const numericValue = Number(normalized)
  const choice = sectionRegistry.variants.find(candidate => (
    candidate.viewType === normalized ||
    (Number.isInteger(numericValue) && candidate.value === numericValue)
  ))
  if (choice) return choice

  // A present but unregistered legacy value belongs to this section's local
  // fallback. Do not let the global compatibility projection reinterpret it
  // as a different selectable path variant.
  if (normalized) return fallback

  return sectionRegistry.variants.find(candidate => candidate.variant === projectedVariant) ?? fallback
}
